import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import StatusBadge from "@/components/StatusBadge";
import { Card } from "@/components/ui/card";
import {
  Package as PackageIcon,
  Buildings,
  Users,
  Receipt,
  TrendUp,
  CurrencyDollar,
  Warning,
  Link as LinkIcon,
  Copy,
  EnvelopeSimple,
  EnvelopeSimpleOpen,
  CheckCircle,
  Scales,
  PencilSimple,
  PaperPlaneTilt,
} from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Toaster, toast } from "sonner";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Area, AreaChart } from "recharts";
import { RateTableEditor } from "@/components/RateTableEditor";
import { RateTableViewer } from "@/components/RateTableViewer";

function StatCard({ label, value, Icon, accent = "#002BF6", testId }) {
  return (
    <div
      className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-md p-5 hover:border-zinc-300 dark:hover:border-zinc-700 transition-colors"
      data-testid={testId}
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.1em] text-zinc-500 dark:text-zinc-400">
            {label}
          </div>
          <div
            className="mt-2 text-3xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-100"
            style={{ fontFamily: "Work Sans" }}
          >
            {value}
          </div>
        </div>
        <div
          className="w-9 h-9 rounded-md flex items-center justify-center"
          style={{ backgroundColor: `${accent}14`, color: accent }}
        >
          <Icon size={18} weight="bold" />
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [series, setSeries] = useState([]);
  const [granularity, setGranularity] = useState("day");
  // Failed-attempts widget data (dispatcher triage)
  const [failed, setFailed] = useState(null);
  // Per-lb shipping rate (customers see their courier's rate)
  const [rate, setRate] = useState(null);
  const [rateViewerOpen, setRateViewerOpen] = useState(false);

  useEffect(() => {
    if (user.role !== "customer") return;
    api.get("/customer/courier-rate").then(({ data }) => setRate(data)).catch(() => {});
  }, [user.role]);

  // Initial load (everything except timeseries which has its own effect)
  useEffect(() => {
    (async () => {
      try {
        const role = user.role;
        const tasks = [
          api.get("/stats/overview"),
          api.get("/packages", { params: { page_size: 6 } }),
          api.get("/companies"),
        ];
        // Failed-attempts widget — only for dispatcher-tier roles
        const canSeeAttempts = ["super_admin", "company_admin", "dispatcher"].includes(role);
        if (canSeeAttempts) tasks.push(api.get("/stats/failed-attempts"));
        const results = await Promise.all(tasks);
        setStats(results[0].data);
        setRecent((results[1].data.items || []).slice(0, 6));
        setCompanies(results[2].data);
        if (canSeeAttempts && results[3]) setFailed(results[3].data);
      } catch (e) {
        // ignore
      }
    })();
  }, [user.role]);

  // Re-fetch timeseries whenever granularity changes
  useEffect(() => {
    (async () => {
      try {
        const days = granularity === "day" ? 14
          : granularity === "week" ? 84       // 12 weeks
          : granularity === "month" ? 365     // 12 months
          : 365 * 5;                          // 5 years
        const t = await api.get("/stats/timeseries", { params: { days, granularity } });
        setSeries(t.data.series || []);
      } catch (e) { /* ignore */ }
    })();
  }, [granularity]);

  const isSuper = user.role === "super_admin";
  const isAdmin = user.role === "super_admin" || user.role === "company_admin";
  const isWarehouse = user.role === "warehouse";

  const companyName = (id) => companies.find((c) => c.id === id)?.name || "—";

  const rangeLabel = granularity === "day" ? "Last 14 days"
    : granularity === "week" ? "Last 12 weeks"
    : granularity === "month" ? "Last 12 months"
    : "Last 5 years";

  const formatTick = (d) => {
    if (!d) return "";
    if (granularity === "day") return d.slice(5);     // MM-DD
    if (granularity === "week") return d.slice(-3);   // Wxx
    if (granularity === "month") return d.slice(5);   // MM
    return d;                                          // YYYY
  };

  return (
    <div className="p-6 sm:p-8 max-w-[1400px]" data-testid="dashboard-page">
      <Toaster position="top-right" richColors />
      <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-4 mb-8">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-500 dark:text-zinc-400">
            {isSuper ? "Platform Overview" : "Workspace"}
          </div>
          <h1
            className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight mt-1 text-zinc-900 dark:text-zinc-100"
            style={{ fontFamily: "Work Sans" }}
          >
            Welcome back, {user.name.split(" ")[0]}.
          </h1>
        </div>
        <div className="text-xs text-zinc-500 dark:text-zinc-400 hidden sm:block">
          {new Date().toLocaleDateString("en-US", {
            weekday: "long",
            month: "long",
            day: "numeric",
            year: "numeric",
          })}
        </div>
      </div>

      {/* Exceptions alert — appears only when there are open exception events */}
      {stats?.exception_count > 0 && (
        <Link
          to="/packages?exceptions=1"
          className="block mb-4 rounded-md border border-red-200 bg-red-50 hover:bg-red-100 dark:bg-red-950/30 dark:border-red-900/50 dark:hover:bg-red-950/50 transition-colors"
          data-testid="exceptions-alert"
        >
          <div className="flex items-center gap-4 px-4 py-3">
            <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/40 flex items-center justify-center text-red-600 dark:text-red-400 shrink-0">
              <Warning size={20} weight="fill" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-red-800 dark:text-red-200">
                {stats.exception_count} package{stats.exception_count === 1 ? "" : "s"} need{stats.exception_count === 1 ? "s" : ""} attention
              </div>
              <div className="text-xs text-red-700 dark:text-red-300 mt-0.5">
                Open exception events flagged on undelivered packages — review and resolve
              </div>
            </div>
            <span className="text-xs font-semibold text-red-700 dark:text-red-300 whitespace-nowrap">
              View →
            </span>
          </div>
        </Link>
      )}

      {/* Stats grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
        {isSuper ? (
          <>
            <StatCard label="Active Tenants" value={stats?.total_companies ?? "—"} Icon={Buildings} accent="#002BF6" testId="stat-companies" />
            <StatCard label="Total Users" value={stats?.total_users ?? "—"} Icon={Users} accent="#09090B" testId="stat-users" />
            <StatCard label="Packages" value={stats?.total_packages ?? "—"} Icon={PackageIcon} accent="#F59E0B" testId="stat-packages" />
            <StatCard label="Revenue (paid)" value={`$${(stats?.revenue ?? 0).toLocaleString()}`} Icon={CurrencyDollar} accent="#10B981" testId="stat-revenue" />
          </>
        ) : (
          <>
            <StatCard label="Total Packages" value={stats?.total_packages ?? "—"} Icon={PackageIcon} accent="#002BF6" testId="stat-packages" />
            <StatCard label="In Transit" value={stats?.in_transit ?? "—"} Icon={TrendUp} accent="#F59E0B" testId="stat-transit" />
            <StatCard label="Delivery Rate" value={`${stats?.delivery_rate ?? 0}%`} Icon={PackageIcon} accent="#10B981" testId="stat-delivery-rate" />
            {isAdmin ? (
              <StatCard label="Outstanding" value={`$${(stats?.outstanding ?? 0).toLocaleString()}`} Icon={Receipt} accent="#EF4444" testId="stat-outstanding" />
            ) : (
              <StatCard label="Delivered" value={stats?.delivered ?? "—"} Icon={PackageIcon} accent="#10B981" testId="stat-delivered" />
            )}
            {user.role === "customer" && (rate?.rate_table?.length > 0 || rate?.per_lb_rate != null) && (
              <button
                type="button"
                onClick={() => setRateViewerOpen(true)}
                className="bg-white dark:bg-zinc-900 border border-cyan-200 dark:border-cyan-900/50 rounded-md p-5 text-left hover:border-cyan-400 dark:hover:border-cyan-700 transition-colors"
                data-testid="stat-rate-table-card"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-[0.1em] text-cyan-700 dark:text-cyan-400">
                      Shipping rates
                    </div>
                    <div
                      className="mt-2 text-xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-100"
                      style={{ fontFamily: "Work Sans" }}
                    >
                      {rate?.rate_table?.length > 0
                        ? `${rate.rate_table.length} weight tier${rate.rate_table.length === 1 ? "" : "s"}`
                        : `${rate.per_lb_rate_currency === "USD" ? "US$" : "J$"}${Number(rate.per_lb_rate).toLocaleString()}/lb`}
                    </div>
                    <div className="text-[11px] text-cyan-700 dark:text-cyan-400 mt-1">View full price list →</div>
                  </div>
                  <div className="w-9 h-9 rounded-md flex items-center justify-center bg-cyan-50 text-cyan-700 dark:bg-cyan-500/10 dark:text-cyan-400">
                    <Scales size={18} weight="bold" />
                  </div>
                </div>
              </button>
            )}
          </>
        )}
      </div>

      {/* Customer signup URL card — company_admin only */}
      {user.role === "company_admin" && user.company_public_slug && (
        <CustomerSignupLinkCard slug={user.company_public_slug} companyName={user.company_name} />
      )}

      {/* Weight-tier shipping rate table — company_admin sets it here, customers see it everywhere */}
      {user.role === "company_admin" && companies[0] && (
        <RateTableCard
          company={companies[0]}
          onUpdated={(c) => setCompanies((prev) => prev.map((x) => (x.id === c.id ? c : x)))}
        />
      )}

      {/* Customer rate-table viewer dialog */}
      {user.role === "customer" && (
        <RateTableViewer
          open={rateViewerOpen}
          onOpenChange={setRateViewerOpen}
          rates={rate?.rate_table || []}
          currency={rate?.per_lb_rate_currency || "JMD"}
          companyName={rate?.company_name}
        />
      )}

      {/* Miami warehouse address — customer only */}
      {user.role === "customer" && (
        <MiamiWarehouseCard user={user} />
      )}

      {/* Verification-nudge tile — super admin only. Shows how many customer
          signups are stuck on the verification step + when the auto-runner last
          fired, plus a one-click "Run now" for ad-hoc flushes. */}
      {isSuper && <VerificationNudgeCard />}

      {/* Failed Delivery Attempts — dispatcher triage inbox */}
      {failed && (failed.awaiting_redelivery?.count > 0 || failed.return_to_sender?.count > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4" data-testid="failed-attempts-widget">
          <FailedAttemptsCard
            tone="amber"
            title="Awaiting redelivery"
            subtitle="1 strike · driver retry needed"
            count={failed.awaiting_redelivery?.count ?? 0}
            samples={failed.awaiting_redelivery?.samples ?? []}
            linkTo="/packages?status=awaiting_redelivery"
            testid="failed-awaiting"
            companyName={companyName}
            isSuper={isSuper}
          />
          <FailedAttemptsCard
            tone="red"
            title="Return to sender"
            subtitle="2 strikes exhausted · returning"
            count={failed.return_to_sender?.count ?? 0}
            samples={failed.return_to_sender?.samples ?? []}
            linkTo="/packages?status=return_to_sender"
            testid="failed-rts"
            companyName={companyName}
            isSuper={isSuper}
          />
        </div>
      )}

      {/* Pipeline + Recent */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-4">
        <div className="lg:col-span-1 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-md p-5">
          <div className="text-xs font-semibold uppercase tracking-[0.1em] text-zinc-500 mb-4">
            Delivery Pipeline
          </div>
          <PipelineRow label="Pending" value={stats?.pending ?? 0} total={stats?.total_packages} color="#71717A" />
          <PipelineRow label="In Transit" value={stats?.in_transit ?? 0} total={stats?.total_packages} color="#F59E0B" />
          <PipelineRow label="Delivered" value={stats?.delivered ?? 0} total={stats?.total_packages} color="#10B981" />

          <div className="mt-6 pt-4 border-t border-zinc-100">
            {isAdmin ? (
              <>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-500">Invoices outstanding</span>
                  <span className="font-semibold text-zinc-900" data-testid="pipeline-outstanding">
                    ${(stats?.outstanding ?? 0).toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between text-sm mt-2">
                  <span className="text-zinc-500">Unpaid invoices</span>
                  <span className="font-semibold text-zinc-900">{stats?.unpaid_invoices ?? 0}</span>
                </div>
              </>
            ) : (
              <div className="flex justify-between text-sm">
                <span className="text-zinc-500">Total packages</span>
                <span className="font-semibold text-zinc-900">{stats?.total_packages ?? 0}</span>
              </div>
            )}
          </div>
        </div>

        <div className="lg:col-span-2 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-md p-5" data-testid="timeseries-chart">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <div className="flex items-center gap-3">
              <div className="text-xs font-semibold uppercase tracking-[0.1em] text-zinc-500">{rangeLabel}</div>
              <div className="hidden sm:flex items-center gap-3 text-xs text-zinc-500">
                <span className="flex items-center"><span className="status-dot" style={{ backgroundColor: "#002BF6" }} />Created</span>
                <span className="flex items-center"><span className="status-dot" style={{ backgroundColor: "#10B981" }} />Delivered</span>
              </div>
            </div>
            <div className="inline-flex rounded-md border border-zinc-200 dark:border-zinc-700 overflow-hidden" data-testid="granularity-tabs">
              {["day", "week", "month", "year"].map((g) => (
                <button
                  key={g}
                  type="button"
                  onClick={() => setGranularity(g)}
                  data-testid={`granularity-${g}`}
                  className={`px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider transition-colors ${
                    granularity === g
                      ? "bg-zinc-900 text-white dark:bg-white dark:text-zinc-900"
                      : "bg-white dark:bg-zinc-900 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800"
                  }`}
                >
                  {g}
                </button>
              ))}
            </div>
          </div>
          <div style={{ width: "100%", height: 220 }}>
            <ResponsiveContainer>
              <AreaChart data={series} margin={{ top: 5, right: 8, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="gCreated" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#002BF6" stopOpacity={0.28} />
                    <stop offset="95%" stopColor="#002BF6" stopOpacity={0.02} />
                  </linearGradient>
                  <linearGradient id="gDelivered" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10B981" stopOpacity={0.28} />
                    <stop offset="95%" stopColor="#10B981" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" />
                <XAxis dataKey="date" tickFormatter={formatTick} stroke="#a1a1aa" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="#a1a1aa" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid #e4e4e7" }} />
                <Area type="monotone" dataKey="created" stroke="#002BF6" strokeWidth={2} fill="url(#gCreated)" />
                <Area type="monotone" dataKey="delivered" stroke="#10B981" strokeWidth={2} fill="url(#gDelivered)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Super-admin revenue chart — JMD across tenants + USD Kanal subscription revenue */}
      {isSuper && <SuperAdminRevenueChart />}

      {/* Recent packages - now separate row */}
      <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-md p-5 mt-4">
        <div className="flex items-center justify-between mb-4">
            <div className="text-xs font-semibold uppercase tracking-[0.1em] text-zinc-500">
              Recent Packages
            </div>
            <Link
              to="/packages"
              className="text-xs font-semibold text-[#002BF6] hover:text-[#0021C7]"
              data-testid="view-all-packages"
            >
              View all →
            </Link>
          </div>
          {recent.length === 0 ? (
            <div className="py-12 text-center text-sm text-zinc-500">
              {user.role === "customer"
                ? "No packages addressed to you yet. Ship to your Miami warehouse address above and they'll show up here."
                : "No packages yet. Create your first package to see it here."}
            </div>
          ) : (
            <div className="divide-y divide-zinc-100">
              {recent.map((p) => (
                <div key={p.id} className="py-3 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <code className="text-xs font-mono text-zinc-900 bg-zinc-100 px-1.5 py-0.5 rounded">
                        {p.tracking_number}
                      </code>
                      {isSuper && (
                        <span className="text-[11px] text-zinc-500">· {companyName(p.company_id)}</span>
                      )}
                    </div>
                    <div className="text-sm text-zinc-700 mt-1 truncate">
                      {p.sender_name} → {p.recipient_name}
                    </div>
                  </div>
                  <StatusBadge status={p.status} />
                </div>
              ))}
            </div>
          )}
      </div>
    </div>
  );
}

function PipelineRow({ label, value, total, color }) {
  const pct = total ? Math.round((value / total) * 100) : 0;
  return (
    <div className="mb-3">
      <div className="flex justify-between text-sm mb-1.5">
        <span className="flex items-center">
          <span className="status-dot" style={{ backgroundColor: color }} />
          <span className="text-zinc-700">{label}</span>
        </span>
        <span className="font-semibold text-zinc-900">{value}</span>
      </div>
      <div className="h-1.5 bg-zinc-100 rounded overflow-hidden">
        <div
          className="h-full transition-all duration-500"
          style={{ width: `${pct}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
}

function VerificationNudgeCard() {
  const [data, setData] = React.useState(null);
  const [stats, setStats] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [running, setRunning] = React.useState(false);

  const load = React.useCallback(async () => {
    try {
      const [statusRes, statsRes] = await Promise.all([
        api.get("/admin/verification/nudge-status"),
        api.get("/admin/verification/conversion-stats?days=30"),
      ]);
      setData(statusRes.data);
      setStats(statsRes.data);
    } catch (e) {
      // Super-admin gated; render nothing on error.
      setData(null);
      setStats(null);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/admin/verification/run-nudges");
      toast.success(`Sent ${data.sent} verification email${data.sent === 1 ? "" : "s"}`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Nudge runner failed.");
    } finally {
      setRunning(false);
    }
  };

  if (loading || !data) return null;

  const pending = data.pending_count ?? 0;
  const eligible = data.eligible_now ?? 0;
  const lastRun = data.last_run?.ran_at;
  const lastSent = data.last_run?.sent ?? 0;

  // Friendly "X minutes/hours/days ago" — falls back to "Never" until first run.
  let lastRunLabel = "Never";
  if (lastRun) {
    const diffMs = Date.now() - new Date(lastRun).getTime();
    const mins = Math.round(diffMs / 60000);
    if (mins < 1) lastRunLabel = "Just now";
    else if (mins < 60) lastRunLabel = `${mins}m ago`;
    else if (mins < 60 * 24) lastRunLabel = `${Math.round(mins / 60)}h ago`;
    else lastRunLabel = `${Math.round(mins / (60 * 24))}d ago`;
  }

  return (
    <div
      className="mt-4 mb-4 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-md p-5"
      data-testid="verification-nudge-card"
    >
      <div className="flex flex-col lg:flex-row lg:items-stretch lg:justify-between gap-4">
        {/* Left: pending queue + actions */}
        <div className="flex items-start gap-4 flex-1 min-w-0">
          <div
            className="w-10 h-10 rounded-md flex items-center justify-center shrink-0"
            style={{ backgroundColor: "#F59E0B14", color: "#B45309" }}
          >
            <EnvelopeSimpleOpen size={22} weight="bold" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-semibold uppercase tracking-[0.1em] text-zinc-500 dark:text-zinc-400">
              Verification queue
            </div>
            <div className="mt-1 flex items-baseline gap-2 flex-wrap">
              <span
                className="text-3xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-100"
                style={{ fontFamily: "Work Sans" }}
                data-testid="verification-pending-count"
              >
                {pending}
              </span>
              <span className="text-sm text-zinc-500">
                customer{pending === 1 ? "" : "s"} awaiting verification
              </span>
            </div>
            <div className="mt-1 text-xs text-zinc-500" data-testid="verification-last-run">
              Last auto-nudge: <span className="font-medium text-zinc-700 dark:text-zinc-300">{lastRunLabel}</span>
              {lastRun && <> · sent {lastSent}</>}
              {" · "}
              <span className="text-zinc-400">
                {eligible} eligible on the next tick
              </span>
            </div>
            <div className="mt-3 flex items-center gap-3">
              <Link
                to="/recipients?verification=pending"
                className="text-xs font-semibold text-zinc-700 dark:text-zinc-300 hover:text-zinc-900 underline-offset-2 hover:underline"
                data-testid="verification-view-pending-link"
              >
                View pending
              </Link>
              <Button
                size="sm"
                onClick={runNow}
                disabled={running || eligible === 0}
                data-testid="verification-run-now-btn"
                className="bg-amber-600 hover:bg-amber-700 text-white"
                title={eligible === 0 ? "Nothing eligible to nudge right now" : "Send a verification email to every eligible unverified customer"}
              >
                <PaperPlaneTilt size={13} className="mr-1.5" weight="bold" />
                {running ? "Sending…" : "Run now"}
              </Button>
            </div>
          </div>
        </div>

        {/* Right: 30-day conversion sparkline */}
        {stats && stats.series && (
          <div
            className="lg:w-[340px] lg:border-l lg:pl-5 lg:border-zinc-100 dark:lg:border-zinc-800 flex flex-col justify-between"
            data-testid="verification-conversion-block"
          >
            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.1em] text-zinc-500 dark:text-zinc-400">
                30-day conversion
              </div>
              <div className="mt-1 flex items-baseline gap-2 flex-wrap">
                <span
                  className="text-3xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-100"
                  style={{ fontFamily: "Work Sans" }}
                  data-testid="verification-conversion-pct"
                >
                  {stats.headline_pct == null ? "—" : `${stats.headline_pct}%`}
                </span>
                <span className="text-xs text-zinc-500">
                  verified within 7d
                </span>
              </div>
              {stats.headline_denominator > 0 ? (
                <div className="text-xs text-zinc-500 mt-1">
                  <span className="font-medium text-zinc-700 dark:text-zinc-300">{stats.headline_numerator}</span>
                  {" of "}
                  <span className="font-medium text-zinc-700 dark:text-zinc-300">{stats.headline_denominator}</span>
                  {" signups (≥ 7 days old)"}
                </div>
              ) : (
                <div className="text-xs text-zinc-400 mt-1">No signups old enough to measure yet</div>
              )}
            </div>
            <div className="h-[64px] mt-3 -mx-1" data-testid="verification-conversion-sparkline">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={stats.series}
                  margin={{ top: 4, right: 4, left: 4, bottom: 0 }}
                >
                  <defs>
                    <linearGradient id="vfSpark" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#F59E0B" stopOpacity={0.45} />
                      <stop offset="100%" stopColor="#F59E0B" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <Tooltip
                    cursor={{ stroke: "#F59E0B", strokeWidth: 1, strokeDasharray: "2 2" }}
                    contentStyle={{ fontSize: 11, padding: "4px 8px" }}
                    formatter={(value, name) => [value, name === "verified_within_7d" ? "verified ≤7d" : "signups"]}
                    labelFormatter={(d) => new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                  />
                  <Area
                    type="monotone"
                    dataKey="signups"
                    stroke="#9CA3AF"
                    strokeWidth={1}
                    fill="none"
                    dot={false}
                    isAnimationActive={false}
                  />
                  <Area
                    type="monotone"
                    dataKey="verified_within_7d"
                    stroke="#F59E0B"
                    strokeWidth={1.75}
                    fill="url(#vfSpark)"
                    dot={false}
                    isAnimationActive={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


function RateTableCard({ company, onUpdated }) {
  const [editorOpen, setEditorOpen] = React.useState(false);
  const [previewOpen, setPreviewOpen] = React.useState(false);
  const tiers = company.rate_table || [];
  const currency = company.per_lb_rate_currency || "JMD";
  const sym = currency === "USD" ? "US$" : "J$";
  const sorted = [...tiers].sort((a, b) => a.lbs - b.lbs);
  const minPrice = sorted[0]?.price;
  const maxPrice = sorted[sorted.length - 1]?.price;

  return (
    <div
      className="mb-6 rounded-md border border-cyan-200 dark:border-cyan-900/50 bg-gradient-to-br from-cyan-50 to-white dark:from-cyan-950/30 dark:to-zinc-900 p-5 sm:p-6"
      data-testid="rate-table-card"
    >
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5">
            <div className="w-7 h-7 rounded-md bg-cyan-600 text-white flex items-center justify-center">
              <Scales size={14} weight="bold" />
            </div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-cyan-700 dark:text-cyan-400">
              Shipping rate table
            </div>
          </div>
          <p className="text-sm text-zinc-700 dark:text-zinc-300">
            Set a price for each package weight from 1 to 100 lbs. Customers see the full table on their dashboard, packages page and pickup form.
          </p>
          {tiers.length > 0 && (
            <div className="mt-2 flex items-center gap-3 text-xs text-zinc-600 dark:text-zinc-400">
              <span>
                <b className="text-zinc-900 dark:text-zinc-100">{tiers.length}</b> tier{tiers.length === 1 ? "" : "s"} set
              </span>
              <span className="text-zinc-300 dark:text-zinc-600">·</span>
              <span data-testid="rate-table-range">
                {sym}{Number(minPrice).toLocaleString()} – {sym}{Number(maxPrice).toLocaleString()} ({currency})
              </span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {tiers.length > 0 && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setPreviewOpen(true)}
              className="border-cyan-300 dark:border-cyan-700/50 text-cyan-700 dark:text-cyan-300 hover:bg-cyan-50 dark:hover:bg-cyan-950/40"
              data-testid="rate-table-preview-btn"
            >
              Preview
            </Button>
          )}
          <Button
            size="sm"
            onClick={() => setEditorOpen(true)}
            className="bg-cyan-600 hover:bg-cyan-700 text-white"
            data-testid="rate-table-edit-btn"
          >
            <PencilSimple size={13} weight="bold" className="mr-1.5" />
            {tiers.length > 0 ? "Edit rate table" : "Set up rate table"}
          </Button>
        </div>
      </div>

      <RateTableEditor
        company={company}
        open={editorOpen}
        onOpenChange={setEditorOpen}
        onSaved={onUpdated}
      />
      <RateTableViewer
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        rates={tiers}
        currency={currency}
        companyName={company.name}
      />
    </div>
  );
}


function CustomerSignupLinkCard({ slug, companyName }) {
  const [copied, setCopied] = React.useState(false);
  const url = `${window.location.origin}/c/${slug}/signup`;
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (e) {
      window.prompt("Copy your customer signup URL:", url);
    }
  };
  const mailto = `mailto:?subject=${encodeURIComponent(
    `Track your packages with ${companyName || "us"} on Kanal`
  )}&body=${encodeURIComponent(
    `Hi,\n\nUse this link to create your customer account with ${companyName || "us"} on Kanal. Once signed up you can track every package and pay invoices online:\n\n${url}\n\nThanks!`
  )}`;
  return (
    <div
      className="mb-6 rounded-md border border-blue-200 dark:border-blue-900/50 bg-gradient-to-br from-blue-50 to-white dark:from-blue-950/30 dark:to-zinc-900 p-5 sm:p-6"
      data-testid="customer-signup-link-card"
    >
      <div className="flex flex-col lg:flex-row lg:items-center gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5">
            <div className="w-7 h-7 rounded-md bg-[#002BF6] text-white flex items-center justify-center">
              <LinkIcon size={14} weight="bold" />
            </div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#002BF6]">
              Your customer signup URL
            </div>
          </div>
          <p className="text-sm text-zinc-700 dark:text-zinc-300 mb-3">
            Share this permanent link with your customers — they can sign up under <b>{companyName}</b>, track every package, and pay invoices online.
          </p>
          <div className="flex items-center gap-2 bg-white dark:bg-zinc-900 border border-blue-200 dark:border-blue-900/40 rounded-md px-3 py-2.5 max-w-full overflow-hidden">
            <LinkIcon size={14} className="text-blue-600 shrink-0" weight="bold" />
            <code
              className="text-xs font-mono text-zinc-900 dark:text-zinc-100 truncate flex-1"
              data-testid="customer-signup-url-text"
              title={url}
            >
              {url}
            </code>
          </div>
        </div>
        <div className="flex sm:flex-row gap-2 lg:flex-col xl:flex-row shrink-0">
          <button
            type="button"
            onClick={onCopy}
            className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-md bg-[#002BF6] text-white text-sm font-medium hover:bg-[#0021C7] transition-colors whitespace-nowrap"
            data-testid="copy-customer-signup-link-btn"
          >
            {copied ? (
              <>
                <CheckCircle size={14} weight="bold" /> Copied!
              </>
            ) : (
              <>
                <Copy size={14} weight="bold" /> Copy link
              </>
            )}
          </button>
          <a
            href={mailto}
            className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-md border border-blue-300 dark:border-blue-700/50 text-blue-700 dark:text-blue-300 text-sm font-medium hover:bg-blue-50 dark:hover:bg-blue-950/40 transition-colors whitespace-nowrap"
            data-testid="share-customer-signup-link-btn"
          >
            <EnvelopeSimple size={14} weight="bold" /> Share
          </a>
        </div>
      </div>
    </div>
  );
}



function MiamiWarehouseCard({ user }) {
  const [copied, setCopied] = React.useState(false);
  const formattedAddress = `${user.name} - ${user.customer_number || "—"}\n2033 W McNab Rd Suite S USA\nPompano Beach, FL 33069\nUSA`;
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(formattedAddress);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (e) {
      window.prompt("Copy your shipping address:", formattedAddress);
    }
  };
  return (
    <div
      className="mb-6 rounded-md border border-emerald-200 dark:border-emerald-900/50 bg-gradient-to-br from-emerald-50 to-white dark:from-emerald-950/30 dark:to-zinc-900 p-5 sm:p-6"
      data-testid="miami-warehouse-card"
    >
      <div className="flex flex-col lg:flex-row lg:items-start gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5">
            <div className="w-7 h-7 rounded-md bg-emerald-600 text-white flex items-center justify-center">
              <Buildings size={14} weight="bold" />
            </div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-emerald-700 dark:text-emerald-400">
              Kanal Miami Warehouse · Air / Ocean shipping
            </div>
          </div>
          <p className="text-sm text-zinc-700 dark:text-zinc-300 mb-3">
            Use this US address when shopping online. Always include your customer number on the second line so we route your packages to <b>{user.company_name}</b>.
          </p>
          <div className="bg-white dark:bg-zinc-900 border border-emerald-200 dark:border-emerald-900/40 rounded-md p-4 font-mono text-sm text-zinc-900 dark:text-zinc-100 leading-relaxed" data-testid="miami-warehouse-address">
            <div><b>{user.name}</b> – <code className="bg-emerald-50 dark:bg-emerald-500/15 px-1.5 py-0.5 rounded text-emerald-700 dark:text-emerald-300 text-xs">{user.customer_number || "(no customer number yet)"}</code></div>
            <div>2033 W McNab Rd Suite S</div>
            <div>Pompano Beach, FL 33414</div>
            <div>USA</div>
          </div>
          <div className="text-[11px] text-zinc-500 dark:text-zinc-400 mt-2">
            Ships via both <b>Air</b> and <b>Ocean</b>. Forgetting your customer number on a parcel delays delivery — copy the address above to keep it accurate.
          </div>
        </div>
        <button
          type="button"
          onClick={onCopy}
          className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-md bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 transition-colors whitespace-nowrap shrink-0 self-start"
          data-testid="copy-miami-address-btn"
        >
          {copied ? (
            <>
              <CheckCircle size={14} weight="bold" /> Copied!
            </>
          ) : (
            <>
              <Copy size={14} weight="bold" /> Copy full address
            </>
          )}
        </button>
      </div>
    </div>
  );
}

/**
 * Failed-attempts dispatcher triage card.
 * Two tones: amber (awaiting_redelivery — 1 strike) and red (return_to_sender — final).
 * Tap any sample row to jump to its package detail; tap the card header to filter
 * /packages by that status.
 */
function FailedAttemptsCard({ tone, title, subtitle, count, samples, linkTo, testid, companyName, isSuper }) {
  const palette = tone === "red"
    ? {
        border: "border-red-200 dark:border-red-500/30",
        bg: "bg-red-50 dark:bg-red-500/10",
        chip: "bg-red-100 text-red-800 dark:bg-red-500/20 dark:text-red-200",
        accent: "text-red-700 dark:text-red-300",
        sample: "hover:bg-red-100/40 dark:hover:bg-red-500/15 border-red-100 dark:border-red-500/20",
      }
    : {
        border: "border-amber-200 dark:border-amber-500/30",
        bg: "bg-amber-50 dark:bg-amber-500/10",
        chip: "bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-200",
        accent: "text-amber-700 dark:text-amber-300",
        sample: "hover:bg-amber-100/40 dark:hover:bg-amber-500/15 border-amber-100 dark:border-amber-500/20",
      };

  return (
    <div className={`rounded-md border ${palette.border} ${palette.bg} overflow-hidden`} data-testid={testid}>
      <a
        href={linkTo}
        className="flex items-start justify-between gap-3 px-5 py-4 border-b border-current/10 hover:opacity-90 transition-opacity"
      >
        <div className="min-w-0">
          <div className={`text-[10px] font-semibold uppercase tracking-[0.18em] ${palette.accent}`}>{title}</div>
          <div className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">{subtitle}</div>
        </div>
        <div className="text-right shrink-0">
          <span className={`inline-flex items-center justify-center min-w-[2rem] px-2 py-1 rounded font-mono text-base font-bold ${palette.chip}`} data-testid={`${testid}-count`}>
            {count}
          </span>
        </div>
      </a>
      {samples.length === 0 ? (
        <div className="px-5 py-6 text-xs text-zinc-500 text-center">No packages in this bucket right now 🎉</div>
      ) : (
        <ul className="divide-y divide-current/5">
          {samples.slice(0, 4).map((p) => (
            <li key={p.id}>
              <a
                href={`/packages/${p.id}`}
                className={`flex items-center justify-between gap-3 px-5 py-2.5 border-l-2 border-transparent ${palette.sample} transition-colors`}
                data-testid={`${testid}-sample-${p.id}`}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <code className="text-[11px] font-mono text-zinc-700 dark:text-zinc-300">{p.tracking_number}</code>
                    {p.signature_required && (
                      <span className="text-[9px] uppercase font-semibold tracking-wider bg-zinc-200 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 rounded px-1.5 py-0.5">SIG</span>
                    )}
                  </div>
                  <div className="text-xs text-zinc-700 dark:text-zinc-200 truncate">{p.recipient_name}</div>
                  {isSuper && (
                    <div className="text-[10px] text-zinc-500 truncate">{companyName(p.company_id)}</div>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <div className={`text-[10px] font-semibold uppercase tracking-wider ${palette.accent}`}>
                    {p.attempts_count} {p.attempts_count === 1 ? "attempt" : "attempts"}
                  </div>
                  {p.last_attempt?.attempted_at && (
                    <div className="text-[10px] text-zinc-500">
                      {new Date(p.last_attempt.attempted_at).toLocaleDateString()}
                    </div>
                  )}
                </div>
              </a>
            </li>
          ))}
        </ul>
      )}
      {count > samples.length && (
        <a
          href={linkTo}
          className={`block px-5 py-2.5 text-xs font-semibold text-center ${palette.accent} hover:underline border-t border-current/5`}
          data-testid={`${testid}-view-all`}
        >
          View all {count} →
        </a>
      )}
    </div>
  );
}


function SuperAdminRevenueChart() {
  const [granularity, setGranularity] = React.useState("day");
  const [series, setSeries] = React.useState([]);
  const [totals, setTotals] = React.useState({ tenant_jmd: 0, kanal_usd: 0 });
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let mounted = true;
    setLoading(true);
    api.get(`/stats/super-admin-revenue?granularity=${granularity}`)
      .then(({ data }) => {
        if (!mounted) return;
        setSeries(data.series || []);
        setTotals(data.totals || { tenant_jmd: 0, kanal_usd: 0 });
      })
      .catch(() => mounted && setSeries([]))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [granularity]);

  const fmtBucket = (b) => {
    if (!b) return "";
    if (granularity === "year") return b;
    if (granularity === "month") return new Date(b + "-01").toLocaleDateString(undefined, { month: "short", year: "2-digit" });
    if (granularity === "week") return b.replace("-W", " W");
    // day
    return new Date(b).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  };

  return (
    <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-md p-5 mt-4" data-testid="super-admin-revenue-chart">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.1em] text-zinc-500">Platform Revenue</div>
          <div className="flex flex-wrap items-baseline gap-x-5 gap-y-1 mt-2">
            <div data-testid="revenue-total-tenant-jmd">
              <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500 mr-1.5">Tenant gross</span>
              <span className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">${Number(totals.tenant_jmd || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
              <span className="text-[10px] font-semibold text-zinc-500 ml-1">JMD</span>
            </div>
            <div data-testid="revenue-total-kanal-usd">
              <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500 mr-1.5">Kanal subs</span>
              <span className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">${Number(totals.kanal_usd || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
              <span className="text-[10px] font-semibold text-zinc-500 ml-1">USD</span>
            </div>
          </div>
        </div>
        <div className="inline-flex rounded-md border border-zinc-200 dark:border-zinc-700 overflow-hidden self-start" data-testid="revenue-granularity-tabs">
          {["day", "week", "month", "year"].map((g) => (
            <button
              key={g}
              type="button"
              onClick={() => setGranularity(g)}
              data-testid={`revenue-granularity-${g}`}
              className={`px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider transition-colors ${
                granularity === g
                  ? "bg-zinc-900 text-white dark:bg-white dark:text-zinc-900"
                  : "bg-white dark:bg-zinc-900 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800"
              }`}
            >
              {g}
            </button>
          ))}
        </div>
      </div>
      <div className="flex items-center gap-4 text-xs text-zinc-500 mb-2">
        <span className="flex items-center"><span className="status-dot" style={{ backgroundColor: "#002BF6" }} />Tenant gross (JMD)</span>
        <span className="flex items-center"><span className="status-dot" style={{ backgroundColor: "#10B981" }} />Kanal subs (USD)</span>
      </div>
      <div style={{ width: "100%", height: 240 }}>
        {loading ? (
          <div className="text-xs text-zinc-400 flex items-center justify-center h-full">Loading…</div>
        ) : series.length === 0 ? (
          <div className="text-xs text-zinc-400 flex items-center justify-center h-full">No paid invoices in this window.</div>
        ) : (
          <ResponsiveContainer>
            <LineChart data={series} margin={{ top: 5, right: 8, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" />
              <XAxis dataKey="bucket" tickFormatter={fmtBucket} stroke="#a1a1aa" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis yAxisId="jmd" stroke="#002BF6" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis yAxisId="usd" orientation="right" stroke="#10B981" fontSize={11} tickLine={false} axisLine={false} />
              <Tooltip
                contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid #e4e4e7" }}
                labelFormatter={fmtBucket}
                formatter={(value, name) => {
                  if (name === "Tenant JMD") return [`$${Number(value).toLocaleString()} JMD`, name];
                  if (name === "Kanal USD") return [`$${Number(value).toLocaleString()} USD`, name];
                  return [value, name];
                }}
              />
              <Line yAxisId="jmd" name="Tenant JMD" type="monotone" dataKey="tenant_jmd" stroke="#002BF6" strokeWidth={2} dot={false} />
              <Line yAxisId="usd" name="Kanal USD" type="monotone" dataKey="kanal_usd" stroke="#10B981" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

